import React, { useRef, useState, useEffect } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'motion/react';
import { ChevronLeft, ChevronRight, Play, X, ExternalLink } from 'lucide-react';

export interface WerseeVideo {
  id: string;
  title: string;
  duration?: string;
  thumbnailUrl: string;
  youtubeUrl: string;
}

export const WERSEE_LONG_FORM_VIDEOS: WerseeVideo[] = [
  {
    id: '6y8qhhsAI5s',
    title: 'Learn Wersee Platform Overview',
    duration: '7:46',
    thumbnailUrl: 'https://i.ytimg.com/vi/6y8qhhsAI5s/hqdefault.jpg',
    youtubeUrl: 'https://www.youtube.com/watch?v=6y8qhhsAI5s',
  },
  {
    id: 'rSFf5xfuE7o',
    title: 'Is Wersee Finance Easy to Use? Full Walkthrough',
    duration: '16:55',
    thumbnailUrl: 'https://i.ytimg.com/vi/rSFf5xfuE7o/hqdefault.jpg',
    youtubeUrl: 'https://www.youtube.com/watch?v=rSFf5xfuE7o',
  },
  {
    id: '6akDv7WIalQ',
    title: 'Wersee Dashboard Walkthrough | Everything in One Place',
    duration: '2:59',
    thumbnailUrl: 'https://i.ytimg.com/vi/6akDv7WIalQ/hqdefault.jpg',
    youtubeUrl: 'https://www.youtube.com/watch?v=6akDv7WIalQ',
  },
  {
    id: 'aJ1PsIYGXX8',
    title: 'Wersee The Developer Ecosystem',
    duration: '6:42',
    thumbnailUrl: 'https://i.ytimg.com/vi/aJ1PsIYGXX8/hqdefault.jpg',
    youtubeUrl: 'https://www.youtube.com/watch?v=aJ1PsIYGXX8',
  },
  {
    id: 'rxUqUPMeHj8',
    title: 'Connect Google Calendar & Gmail with OAuth',
    duration: '0:42',
    thumbnailUrl: 'https://i.ytimg.com/vi/rxUqUPMeHj8/hqdefault.jpg',
    youtubeUrl: 'https://www.youtube.com/watch?v=rxUqUPMeHj8',
  },
  {
    id: '8Damg-7RFX4',
    title: 'Create a Payment Link in Under 60 Seconds',
    duration: '1:25',
    thumbnailUrl: 'https://i.ytimg.com/vi/8Damg-7RFX4/hqdefault.jpg',
    youtubeUrl: 'https://www.youtube.com/watch?v=8Damg-7RFX4',
  },
  {
    id: '4Z1AAruTXHY',
    title: 'What Happens When You Finally Make Your Move?',
    duration: '0:57',
    thumbnailUrl: 'https://i.ytimg.com/vi/4Z1AAruTXHY/hqdefault.jpg',
    youtubeUrl: 'https://www.youtube.com/watch?v=4Z1AAruTXHY',
  },
];

const SOCIAL_LINKS = [
  {
    name: 'Instagram',
    url: 'https://www.instagram.com/werseeapp',
    svg: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8">
        <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
        <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
        <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
      </svg>
    ),
  },
  {
    name: 'YouTube',
    url: 'https://www.youtube.com/@werseeapp',
    svg: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8">
        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
      </svg>
    ),
  },
  {
    name: 'TikTok',
    url: 'https://www.tiktok.com/@werseeapp',
    svg: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8">
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64c.29 0 .58.04.86.12V9.42a6.34 6.34 0 0 0-.86-.06 6.34 6.34 0 1 0 6.34 6.34V8.58a8.28 8.28 0 0 0 4.77 1.52V6.69z" />
      </svg>
    ),
  },
  {
    name: 'X',
    url: 'https://x.com/werseeapp',
    svg: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
      </svg>
    ),
  },
];

export function HomeSocialsSection() {
  const containerRef = useRef<HTMLDivElement>(null);
  const sliderRef = useRef<HTMLDivElement>(null);
  const [activeVideo, setActiveVideo] = useState<WerseeVideo | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start end', 'end start'],
  });

  // Logos animate upwards during scroll and then animate/fade away
  const logosY = useTransform(scrollYProgress, [0.08, 0.28, 0.48], [40, -10, -95]);
  const logosOpacity = useTransform(scrollYProgress, [0.05, 0.18, 0.34, 0.46], [0.3, 1, 0.95, 0]);
  const logosScale = useTransform(scrollYProgress, [0.08, 0.28, 0.48], [0.96, 1, 0.88]);

  // Video slider smoothly transitions in as logos animate away
  const sliderOpacity = useTransform(scrollYProgress, [0.24, 0.42], [0.2, 1]);
  const sliderY = useTransform(scrollYProgress, [0.24, 0.42], [30, 0]);

  const updateScrollButtons = () => {
    const el = sliderRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 10);
  };

  useEffect(() => {
    const el = sliderRef.current;
    if (!el) return;
    const observer = new ResizeObserver(updateScrollButtons);
    observer.observe(el);
    updateScrollButtons();
    return () => observer.disconnect();
  }, []);

  const scrollBy = (offset: number) => {
    const el = sliderRef.current;
    if (!el) return;
    el.scrollBy({ left: offset, behavior: 'smooth' });
  };

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setActiveVideo(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <section ref={containerRef} className="ws-socials-section relative py-24 sm:py-32 overflow-hidden" aria-label="Wersee Socials and Videos">
      {/* Logos Stage: animate upwards on scroll and fade out */}
      <motion.div
        style={{ y: logosY, opacity: logosOpacity, scale: logosScale }}
        className="ws-socials-logos-stage flex items-center justify-center gap-10 sm:gap-16 mb-16 pointer-events-auto"
      >
        {SOCIAL_LINKS.map((social) => (
          <a
            key={social.name}
            href={social.url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={social.name}
            className="text-zinc-500 hover:text-zinc-200 transition-colors duration-300 p-3 rounded-full hover:bg-white/[0.04]"
          >
            {social.svg}
          </a>
        ))}
      </motion.div>

      {/* 16:9 Long-form Videos Slider */}
      <motion.div
        style={{ opacity: sliderOpacity, y: sliderY }}
        className="ws-wrap relative"
      >
        <div className="flex items-center justify-between mb-8 px-2">
          <div className="flex items-center gap-3">
            <a
              href="https://www.youtube.com/@werseeapp"
              target="_blank"
              rel="noopener noreferrer"
              className="text-lg sm:text-xl font-medium tracking-tight text-zinc-300 hover:text-white transition-colors inline-flex items-center gap-2"
            >
              @werseeapp
              <ExternalLink className="w-3.5 h-3.5 opacity-60" />
            </a>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => scrollBy(-420)}
              disabled={!canScrollLeft}
              aria-label="Previous videos"
              className="w-10 h-10 rounded-full border border-white/10 bg-white/[0.03] text-zinc-400 hover:text-white hover:bg-white/[0.08] flex items-center justify-center transition disabled:opacity-20 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={() => scrollBy(420)}
              disabled={!canScrollRight}
              aria-label="Next videos"
              className="w-10 h-10 rounded-full border border-white/10 bg-white/[0.03] text-zinc-400 hover:text-white hover:bg-white/[0.08] flex items-center justify-center transition disabled:opacity-20 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* 16:9 Slider Track */}
        <div
          ref={sliderRef}
          onScroll={updateScrollButtons}
          tabIndex={0}
          role="region"
          aria-label="Wersee long form video slider"
          className="ws-video-slider flex gap-6 overflow-x-auto scroll-smooth pb-6 scrollbar-none outline-none focus-visible:ring-1 focus-visible:ring-zinc-500 rounded-3xl"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {WERSEE_LONG_FORM_VIDEOS.map((video) => (
            <motion.div
              key={video.id}
              whileHover={{ y: -6 }}
              transition={{ duration: 0.2 }}
              onClick={() => setActiveVideo(video)}
              className="group flex-shrink-0 w-[300px] sm:w-[380px] md:w-[440px] cursor-pointer"
            >
              {/* 16:9 Video Card */}
              <div className="relative aspect-[16/9] w-full rounded-2xl overflow-hidden bg-zinc-900 border border-white/10 shadow-xl shadow-black/40">
                <img
                  src={video.thumbnailUrl}
                  alt=""
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/30 group-hover:bg-black/10 transition-colors" />

                {/* Minimalist Play Icon */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-13 h-13 sm:w-14 sm:h-14 rounded-full bg-white/20 backdrop-blur-md border border-white/30 flex items-center justify-center text-white shadow-2xl transition-all duration-300 group-hover:scale-110 group-hover:bg-white group-hover:text-black">
                    <Play className="w-6 h-6 fill-current ml-0.5" />
                  </div>
                </div>

                {/* Duration */}
                {video.duration && (
                  <div className="absolute bottom-3 right-3 px-2 py-1 rounded-md bg-black/80 backdrop-blur-sm text-[11px] font-mono text-zinc-300">
                    {video.duration}
                  </div>
                )}
              </div>

              {/* Video Title */}
              <div className="mt-3.5 px-1">
                <p className="text-sm sm:text-base font-medium text-zinc-300 group-hover:text-white line-clamp-2 transition-colors">
                  {video.title}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Video Modal Player */}
      <AnimatePresence>
        {activeVideo && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActiveVideo(null)}
            className="fixed inset-0 z-50 bg-black/90 backdrop-blur-xl flex items-center justify-center p-4 sm:p-8"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-5xl bg-zinc-950 rounded-3xl overflow-hidden border border-white/10 shadow-2xl"
            >
              <div className="flex items-center justify-between p-4 px-6 border-b border-white/10">
                <span className="text-sm font-medium text-zinc-300 truncate max-w-md">
                  {activeVideo.title}
                </span>
                <div className="flex items-center gap-4">
                  <a
                    href={activeVideo.youtubeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-zinc-400 hover:text-white transition inline-flex items-center gap-1"
                  >
                    YouTube <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                  <button
                    type="button"
                    onClick={() => setActiveVideo(null)}
                    aria-label="Close video"
                    className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="relative aspect-[16/9] w-full bg-black">
                <iframe
                  src={`https://www.youtube-nocookie.com/embed/${activeVideo.id}?autoplay=1&rel=0`}
                  title={activeVideo.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full border-0"
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
